import { reaction } from 'mobx'
import { useEffect } from 'react'
import { useRunState } from '../../provider/useRunState'
import type { TrackController, TrackRegionState } from '../track/trackController'
import { Group } from 'three'

// TODO: Whole thing could use refactor

export type TransitionRange = {
    key: 'transition1' | 'transition2' | 'transition3'
    start: number
    end: number
}

export function getTransitionRanges(
    trackController: TrackController,
): TransitionRange[] {
    const resolvedRegions = trackController.getResolvedTrackRegionConfig()
    return [
        { key: 'transition1', ...resolvedRegions.transition1 },
        { key: 'transition2', ...resolvedRegions.transition2 },
        { key: 'transition3', ...resolvedRegions.transition3 },
    ]
}

export function useTunnelTransitionVisibility(
    group: Group | null,
    objectNamesForTransition: (
        transition: 1 | 2 | 3,
        part: 'entrance' | 'inside' | 'exit',
    ) => readonly string[],
) {
    const runState = useRunState()

    useEffect(() => {
        if (!group) {
            return
        }

        const syncVisibility = () => {
            setTransitionVisibility(
                group,
                objectNamesForTransition,
                resolveTunnelVisibility(runState.trackRegionState),
            )
        }

        syncVisibility()

        return reaction(
            () => [
                runState.trackRegionState.activeRegion,
                runState.trackRegionState.activeTransition,
            ] as const,
            syncVisibility,
        )
    }, [group, objectNamesForTransition, runState])
}

function setTransitionVisibility(
    group: Group,
    objectNamesForTransition: (
        transition: 1 | 2 | 3,
        part: 'entrance' | 'inside' | 'exit',
    ) => readonly string[],
    visibility: ReadonlyMap<
        1 | 2 | 3 | 4,
        { entrance: boolean; inside: boolean; exit: boolean }
    >,
) {
    for (const transition of [1, 2, 3] as const) {
        const parts = visibility.get(transition) ?? {
            entrance: false,
            inside: false,
            exit: false,
        }
        for (const part of ['entrance', 'inside', 'exit'] as const) {
            for (const objectName of objectNamesForTransition(transition, part)) {
                const object = group.getObjectByName(objectName)
                if (!object) {
                    continue
                }
                object.visible = parts[part]
            }
        }
    }
}

function resolveTunnelVisibility(regionState: TrackRegionState) {
    const visibility = new Map<
        1 | 2 | 3 | 4,
        { entrance: boolean; inside: boolean; exit: boolean }
    >([
        [1, { entrance: false, inside: false, exit: false }],
        [2, { entrance: false, inside: false, exit: false }],
        [3, { entrance: false, inside: false, exit: false }],
    ])

    if (regionState.activeTransition) {
        visibility.set(regionState.activeRegion, {
            entrance: true,
            inside: true,
            exit: true,
        })

        return visibility
    }

    visibility.set(regionState.activeRegion, {
        entrance: true,
        inside: false,
        exit: false,
    })

    return visibility
}
